import Layout from "@/components/Layout";
import { SectionLabel, SectionTitle } from "@/components/Section";

const LastUpdated = () => (
  <p className="text-sm text-muted-foreground font-body mb-8">
    Last updated: May 24, 2026
  </p>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h2 className="font-display text-xl text-navy-deep mt-8 mb-3">{children}</h2>
);

const Body = ({ children }: { children: React.ReactNode }) => (
  <p className="text-text-light font-body leading-relaxed mb-4">{children}</p>
);

const List = ({ items }: { items: string[] }) => (
  <ul className="list-disc list-inside text-text-light font-body leading-relaxed mb-4 space-y-1">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const Privacy = () => (
  <Layout>
    <div className="bg-gradient-to-b from-white via-off-white to-white min-h-screen">
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 pt-32 pb-20">
        <SectionLabel>Legal</SectionLabel>
        <SectionTitle>Privacy Policy</SectionTitle>
        <LastUpdated />

        <SectionHeading>1. Introduction</SectionHeading>
        <Body>
          Velocity Offshore Holdings LLC ("Velocity Offshore", "we", "us", or "our") is committed 
          to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and 
          safeguard your information when you visit our website at www.velocityoffshore.com (the "Site"), 
          use our Client Portal or Employee Portal, or otherwise interact with us. Please read this 
          policy carefully. If you do not agree with the terms of this Privacy Policy, please do not 
          access the Site.
        </Body>

        <SectionHeading>2. Information We Collect</SectionHeading>
        <Body>
          We may collect information about you in a variety of ways, including:
        </Body>
        <List items={[
          "Personal Data: Name, email address, telephone number, employer, and job title when you contact us, request information, or register for portal access.",
          "Derivative Data: Information our servers automatically collect when you access the Site, such as your IP address, browser type, operating system, access times, and pages viewed.",
          "Portal Data: For authorised users of our Client and Employee Portals, we collect login credentials, role assignments, activity logs, document access records, and operational data necessary for platform functionality.",
          "Cookies and Tracking Technologies: We use cookies and similar tracking technologies to track activity on our Site and hold certain information. See our Cookies Policy for details.",
        ]} />

        <SectionHeading>3. How We Use Your Information</SectionHeading>
        <Body>
          Having accurate information about you permits us to provide you with a smooth, efficient, 
          and customised experience. Specifically, we may use information collected about you via the 
          Site to:
        </Body>
        <List items={[
          "Create and manage your account and portal access.",
          "Respond to your inquiries and fulfil your service requests.",
          "Send you technical notices, updates, security alerts, and support messages.",
          "Monitor and analyse usage and trends to improve your experience with the Site.",
          "Comply with legal obligations and enforce our terms and policies.",
          "Prevent fraudulent transactions, monitor against theft, and protect against criminal activity.",
        ]} />

        <SectionHeading>4. Disclosure of Your Information</SectionHeading>
        <Body>
          We may share information we have collected about you in certain situations. Your information 
          may be disclosed as follows:
        </Body>
        <List items={[
          "By Law or to Protect Rights: If we believe disclosure is necessary to respond to legal process, investigate potential violations of our policies, or protect the rights, property, and safety of others.",
          "Business Transfers: In connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.",
          "With Consent: We may disclose your personal information for any other purpose with your consent.",
        ]} />
        <Body>
          We do not sell, trade, rent, or otherwise share your personal information with third parties 
          for their direct marketing purposes.
        </Body>

        <SectionHeading>5. Data Security</SectionHeading>
        <Body>
          We use administrative, technical, and physical security measures to help protect your personal 
          information. While we have taken reasonable steps to secure the personal information you provide 
          to us, please be aware that despite our efforts, no security measures are perfect or impenetrable, 
          and no method of data transmission can be guaranteed against any interception or other type 
          of misuse.
        </Body>

        <SectionHeading>6. Data Retention</SectionHeading>
        <Body>
          We will retain your personal information only for as long as is necessary for the purposes 
          set out in this Privacy Policy, to the extent necessary to comply with our legal obligations, 
          resolve disputes, and enforce our policies. Portal activity logs and document access records 
          may be retained for audit and compliance purposes in accordance with maritime industry standards 
          and regulatory requirements.
        </Body>

        <SectionHeading>7. Your Data Protection Rights</SectionHeading>
        <Body>
          Depending on your location, you may have the following rights regarding your personal information:
        </Body>
        <List items={[
          "The right to access – You have the right to request copies of your personal data.",
          "The right to rectification – You have the right to request that we correct any information you believe is inaccurate or incomplete.",
          "The right to erasure – You have the right to request that we erase your personal data, under certain conditions.",
          "The right to restrict processing – You have the right to request that we restrict the processing of your personal data, under certain conditions.",
          "The right to data portability – You have the right to request that we transfer the data we have collected to another organisation, or directly to you, under certain conditions.",
        ]} />
        <Body>
          To exercise any of these rights, please contact us using the details provided below.
        </Body>

        <SectionHeading>8. International Data Transfers</SectionHeading>
        <Body>
          Velocity Offshore operates globally, with servers and personnel located in multiple jurisdictions. 
          Your information may be transferred to, and maintained on, computers located outside of your 
          state, province, country, or other governmental jurisdiction where the data protection laws may 
          differ from those in your jurisdiction. We take appropriate safeguards to ensure that your personal 
          data receives an adequate level of protection in the jurisdictions in which we process it.
        </Body>

        <SectionHeading>9. Children's Privacy</SectionHeading>
        <Body>
          The Site is not intended for children under the age of 18. We do not knowingly collect personal 
          information from children under 18. If you are a parent or guardian and believe that your child 
          has provided personal information on our Site, please contact us immediately.
        </Body>

        <SectionHeading>10. Changes to This Privacy Policy</SectionHeading>
        <Body>
          We may update this Privacy Policy from time to time. The updated version will be indicated by an 
          updated "Last updated" date. We encourage you to review this Privacy Policy frequently to stay 
          informed of how we are protecting your information.
        </Body>

        <SectionHeading>11. Contact Us</SectionHeading>
        <Body>
          If you have any questions or concerns about this Privacy Policy, please contact us through the 
          Contact page on this Site or write to Velocity Offshore Holdings LLC, Majuro, Marshall Islands.
        </Body>
      </div>
    </div>
  </Layout>
);

export default Privacy;
